import { useEffect } from "react";
import { BrowserRouter, Link, Route, Routes, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { ArrowRight } from "lucide-react";
import { LanguageProvider, useLang } from "@/i18n";
import { ALL_PAGES } from "@/content";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageRenderer } from "@/components/PageRenderer";
import { KycVerify } from "@/components/KycVerify";
import { Seo } from "@/components/Seo";
import Home from "@/pages/Home";
import Admin from "@/pages/Admin";
import InsightsPage from "@/pages/InsightsPage";
import ArticlePage from "@/pages/ArticlePage";
import ContactPage from "@/pages/ContactPage";
import AmlCryptoPage from "@/pages/AmlCryptoPage";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const ContentPage = ({ page }) => (
  <>
    <Header />
    <main>
      <PageRenderer page={page} />
    </main>
    <Footer />
  </>
);

const withLayout = (el) => (
  <>
    <Header />
    <main>{el}</main>
    <Footer />
  </>
);

const amlLinkStrings = {
  fr: { label: "AML Crypto — Partenaire de vérification", sub: "Vérification d'adresse crypto & dépôt de documents KYC" },
  en: { label: "AML Crypto — Verification partner", sub: "Crypto address verification & KYC document upload" },
  de: { label: "AML Crypto — Verifizierungspartner", sub: "Krypto-Adressverifizierung & KYC-Dokumentenupload" },
};

const VerificationPage = () => {
  const { lang } = useLang();
  const t = amlLinkStrings[lang] || amlLinkStrings.fr;
  return (
    <div className="flex min-h-screen justify-center bg-[#0A1F18] px-4 pb-24 pt-40">
      <div className="w-full max-w-xl">
        <KycVerify />
        <Link
          to="/aml-crypto"
          data-testid="aml-crypto-link"
          className="group mt-6 flex items-center justify-between border border-[#1E4437] bg-[#102920] p-6 transition-colors duration-300 hover:border-[#C87D55]"
        >
          <div>
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#B0C2B9]">{t.sub}</span>
            <p className="mt-1 font-display text-lg font-semibold text-[#FBF8F3]">{t.label}</p>
          </div>
          <ArrowRight className="h-5 w-5 shrink-0 text-[#C87D55] transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </div>
    </div>
  );
};

function App() {
  return (
    <LanguageProvider>
      <HelmetProvider>
        <BrowserRouter>
          <Seo />
          <ScrollToTop />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/insights" element={withLayout(<InsightsPage />)} />
          <Route path="/insights/:slug" element={withLayout(<ArticlePage />)} />
          <Route path="/contact" element={withLayout(<ContactPage />)} />
          <Route path="/verification" element={<VerificationPage />} />
          <Route path="/aml-crypto" element={withLayout(<AmlCryptoPage />)} />
          {ALL_PAGES.map((page) => (
            <Route key={page.slug} path={`/${page.slug}`} element={<ContentPage page={page} />} />
          ))}
          <Route path="*" element={<Home />} />
          </Routes>
        </BrowserRouter>
      </HelmetProvider>
      <Toaster position="top-center" richColors />
    </LanguageProvider>
  );
}

export default App;
